/* eslint-disable no-unused-vars */
import msgpack from 'msgpack-lite';
import store from "@/store/store";
import Mutations from '@/store/mutations';
import Node, { NodeType } from '@/models/Node';
import Timeline from '@/models/Timeline';
import { Buffer } from 'buffer';
import Compression from '@/util/Compression';
import Tag from '@/models/Tag';
import Vector2 from '@/models/Vector2';
import Project from '@/models/Project';
import Connection from '@/models/Connection';
import { SchemaVersion, GetSchemaDisplayVersion, IsSchemaCompatible } from '@/util/embed_schema';
import { EmbedBaseURL, EmbedShouldUseSchemaInURL } from '@/constants/environment';
import DateOptions from '@/models/DateOptions';

export function EmbedBaseURLWithSchema(schemaVersion : number)
{
    if (!EmbedShouldUseSchemaInURL)
    {
        return EmbedBaseURL;
    }
    
    // If we're using a compatible schema, use the latest version
    return EmbedBaseURL + 'v' + GetSchemaDisplayVersion(schemaVersion.toString()) + '/';
}

export function ActiveEmbedBaseURL()
{
    if (!EmbedShouldUseSchemaInURL)
    {
        return EmbedBaseURL;
    }
    return EmbedBaseURL + 'v' + SchemaVersion() + '/';
}

class SerializedProject
{
    constructor(proj : Project | null)
    {
        if (proj == null) return;
        this.id = proj.id;
        this.name = proj.name;
        this.description = proj.description;
        this.creation = proj.creation.toString();
        this.data = proj.data;
    }

    id = ""
    name = ""
    description = ""
    creation : string = new Date().toString()
    data = ""
}

class SerializedTimeline
{
    constructor(tl : Timeline)
    {
        this.name = tl.name;
        this.description = tl.description;
        this.comments = tl.comments;
        this.id = tl.id;

        for(const i of tl.nodeIDs)
        {
            this.nodeIDs.push(i);
        }

        for(const tag of tl.tags)
        {
            this.tagIDs.push(tag.tagid);
        }

        tl.eventOrderings.forEach((val, key)=>{
            this.eventOrderings.push({ key: key, value: val });
        });
    }

    name = ""
    description = ""
    comments = ""
    id = -1
    nodeIDs: number[] = []
    tagIDs: number[] = []
    eventOrderings: { key: string, value: number[] }[] = []
}

class SerializedNode
{
    constructor(node : Node)
    {
        this.nodeid = node.nodeid;
        this.type = node.type;
        this.name = node.name;
        this.important = node.important;
        this.description = node.description;
        this.references = node.references;
        this.image = node.image;
        this.video = node.video;
        this.imageCaption = node.imageCaption;
        this.audio = node.audio;
        this.dateStart = node.dateStart.valueOf();
        this.dateEnd = node.dateEnd.valueOf();
        this.dateOptions = node.dateOptions.valueOf();
        this.position = node.position;
        this.color = node.color;
        this.data = JSON.stringify(node.data);
        
        for(const tag of node.tags)
        {
            this.tagIDs.push(tag.tagid);
        }

        for(const n of node.relatedArray)
        {
            this.relatedNodes.push(n.nodeid);
        }
    }

    nodeid = -1
    type = NodeType.TimelineEvent
    name = ''
    important = false
    description = ''
    references = ''
    image = ''
    video = ''
    imageCaption = ''
    audio = ''
    dateStart : string | number = new Date(0).toString()
    dateEnd : string | number = new Date(0).toString()
    dateOptions : number = 0
    tagIDs: number[] = []
    position: Vector2 = new Vector2(0, 0)
    relatedNodes: number[] = []
    color = { r: 0, g: 0, b: 0 }
    data = ''
}

class SerializedSnapshot
{
    project : SerializedProject = new SerializedProject(null);
    timelines: SerializedTimeline[] = [];
    nodes: SerializedNode[] = [];
    tags: SerializedTag[] = [];
    connections: Connection[] = []
}

function proj_deserialize(_this : SerializedProject, tg : Project)
{
    tg.id = _this.id;
    tg.name = _this.name;
    tg.description = _this.description;
    tg.creation = new Date(_this.creation);
    tg.data = _this.data;
}

function tl_deserialize(_this : SerializedTimeline, tg : Timeline, globalTags : Map<number, Tag>)
{
    tg.name = _this.name;
    tg.description = _this.description;
    tg.comments = _this.comments;
    tg.id = _this.id;
    if (_this.eventOrderings)
    {
        for(const order of _this.eventOrderings)
        {
            tg.eventOrderings.set(order.key, order.value);
        }
    }
    
    tg.nodeIDs.length = 0;
    for(const id of _this.nodeIDs)
    {
        tg.nodeIDs.push(id);
    }

    tg.tags.length = 0;
    for(const id of _this.tagIDs)
    {
        const tag = globalTags.get(id);
        if (!tag)
        {
            throw {
                error: "Invalid tag in timeline",
                detail:
                {
                    tl_id: _this.id,
                    tl_name: _this.name,
                    tag_id: id
                }
            };
        }
        tg.tags.push(tag);
    }
}

function node_deserialize_norelated(_this : SerializedNode, tg : Node, globalTags : Map<number, Tag>)
{
    tg.nodeid = _this.nodeid;
    tg.type = _this.type;
    tg.name = _this.name;
    tg.important = _this.important;
    tg.description = _this.description;
    tg.references = _this.references;
    tg.image = _this.image;
    tg.video = _this.video;
    tg.imageCaption = _this.imageCaption;
    tg.audio = _this.audio;

    tg.dateStart = new Date(_this.dateStart);
    tg.dateEnd = new Date(_this.dateEnd);
    tg.dateOptions = new DateOptions(_this.dateOptions);

    tg.position = _this.position;
    tg.color = _this.color;

    for(const id of _this.tagIDs)
    {
        const tag = globalTags.get(id);
        if (!tag)
        {
            throw {
                error: "Invalid tag in node",
                detail:
                {
                    node_id: _this.nodeid,
                    node_name: _this.name,
                    tag_id: id
                }
            };
        }
        tg.tags.push(tag);
    }

    console.log('node start');
    console.log(_this.data);
    console.log('node end');
    try
    {
        tg.data = JSON.parse(_this.data);
    }
    catch(err)
    {
        tg.data = {};
        console.error('Node data parse error, printed below');
        console.error(err);
    }
}

function node_initialize_related(_this : SerializedNode, tg : Node, nodes : Map<number, Node>)
{
    for(const id of _this.relatedNodes)
    {
        const node = nodes.get(id);
        if (!node)
        {
            throw {
                error: "Invalid connection",
                detail:
                {
                    node_id: _this.nodeid,
                    node_name: _this.name,
                    related_id: id
                }
            };
        }
        tg.relatedArray.push(node);
    }
}

class SerializedTag
{
    constructor(tag : Tag)
    {
        this.tagid = tag.tagid;
        this.type = tag.type;
        this.name = tag.name;
        this.color = tag.color;
    }
    
    tagid = -1
    type = ""
    name = ""
    color = { r: 0, g: 0, b: 0 }
}

function tag_deserialize(_this : SerializedTag, tg : Tag)
{
    tg.tagid = _this.tagid;
    tg.type = _this.type;
    tg.name = _this.name;
    tg.color = _this.color;
}

export function serializeVuexStore(timelineIds : number[] | undefined = undefined, singleTimeline : Boolean = false) : string
{
    const globalTags = (<Map<number, Tag>>((store.state as any).project.globalTags));
    const nodes : Map<number, Node> = new Map<number, Node>();
    const writeTimelines : SerializedTimeline[] = [];

    if (timelineIds == undefined)
    {
        for(const tl of (<Map<number, Timeline>>(store.state as any).project.timelines))
        {
            writeTimelines.push(new SerializedTimeline(tl[1]));
        }
    }
    else
    {
        for(const tl of (<Map<number, Timeline>>(store.state as any).project.timelines))
        {
            if (timelineIds.includes(tl[1].id))
            {
                writeTimelines.push(new SerializedTimeline(tl[1]));
            }
        }
    }

    const unfilteredSrcNodes = (<Map<number, Node>>(store.state as any).project.nodes);
    for(const tl of writeTimelines)
    {
        for(const nodeid of tl.nodeIDs)
        {
            if (!nodes.has(nodeid))
            {
                const node = unfilteredSrcNodes.get(nodeid);
                if (node == undefined)
                {
                    console.error(`Invalid node found while serializing timeline "${tl.name}"`);
                    continue;
                }
                nodes.set(nodeid, node);
            }
        }
    }

    const writeNodes : SerializedNode[] = [];
    for(const node of nodes)
    {
        writeNodes.push(new SerializedNode(node[1]));
    }

    const tags : Map<number, Tag> = new Map<number, Tag>();
    for(const node of writeNodes)
    {
        for(const tagid of node.tagIDs)
        {
            const tag = globalTags.get(tagid);
            if (!tag) continue;

            tags.set(tagid, tag);
        }
    }

    for(const tl of writeTimelines)
    {
        for(const tagid of tl.tagIDs)
        {
            const tag = globalTags.get(tagid);
            if (!tag) continue;

            tags.set(tagid, tag);
        }
    }

    const writeTags : SerializedTag[] = [];
    for(const tag of tags)
    {
        writeTags.push(new SerializedTag(tag[1]));
    }

    const globalConn : Map<number, Map<number, Connection>> = (store.state as any).project.connections;
    const connections : Connection[] = [];

    for(const kv of globalConn)
    {
        for(const nkv of kv[1])
        {
            const conn = nkv[1];
            if (nodes.has(conn.nodeid1) && nodes.has(conn.nodeid2))
            {
                connections.push(conn);
            }
        }
    }

    const unencodedPayload = {
        project: new SerializedProject((store.state as any).project.project as Project),
        timelines: writeTimelines,
        nodes: writeNodes,
        tags: writeTags,
        connections
    };
    console.log("Payload ================");
    console.log(unencodedPayload);
    console.log("/Payload ================");
    
    const payload = msgpack.encode
    (
        unencodedPayload
    );

    // TODO: This can probably be made more memory efficient
    const toCompressBuffer = Buffer.from(payload);
    const compressed = Compression.compress(toCompressBuffer);

    // Allocate a buffer with length (payload.length) + schemaVersion + uncompressed length int + isSingle bool
    let finalBuffer = Buffer.alloc(toCompressBuffer.length + 4 + 4 + 1);
    let offset = 0;
    offset = finalBuffer.writeInt32LE(SchemaVersion(), offset);
    offset = finalBuffer.writeInt32LE(payload.byteLength, offset);
    offset = finalBuffer.writeInt8(singleTimeline ? 1 : 0, offset);

    for(const v of compressed.values())
    {
        offset = finalBuffer.writeUInt8(v, offset);
    }
    finalBuffer = finalBuffer.slice(0, offset);

    return finalBuffer.toString("base64");
}

export function deserialize(encoded : string)
{
    const raw = Buffer.from(encoded, 'base64');
    let offset = 0;
    const version = raw.readInt32LE(offset);
    offset += 4;

    if (!IsSchemaCompatible(version.toString()))
    {
        throw {
            error: "Embedded data does not match client version",
            detail:
            {
                expected: SchemaVersion(),
                actual: version
            }
        };
    }
    
    const len = raw.readInt32LE(offset);
    offset += 4;
    
    const isSingleTimeline = (raw.readInt8(offset) == 1);
    offset += 1;
    // TODO: Disable navbar if it is a single timeline

    const compressed = raw.slice(offset, raw.length);
    const uncompressed = Compression.decompress(compressed);
    if (uncompressed.length != len)
    {
        throw {
            error: "Payload size mismatch", 
            detail:
            {
                expected: len,
                actual: uncompressed.length
            }
        };
    }

    const data : SerializedSnapshot = msgpack.decode(uncompressed);
    return data;
}

export function deserializeDataIntoVuexStore(data : SerializedSnapshot)
{
    store.commit(Mutations.resetProjectToDefault);
    for(const stag of data.tags)
    {
        const tg = new Tag;
        tag_deserialize(stag, tg);
        store.commit(Mutations.setTag, tg);
    }

    console.log("Early print");
    console.log(data.nodes);
    
    const globalTags = (<Map<number, Tag>>((store.state as any).project.globalTags));
    for(const snode of data.nodes)
    {
        const tg = new Node;
        node_deserialize_norelated(snode, tg, globalTags);
        store.commit(Mutations.setNode, tg);
    }
    
    const nodes = (<Map<number, Node>>((store.state as any).project.nodes));
    console.log("Post insert");
    console.log(nodes);
    for(const snode of data.nodes)
    {
        const tg = nodes.get(snode.nodeid);
        if (!tg)
        {
            throw {
                error: "Attempted to init relations for Invalid node",
                detail:
                {
                    node_id: snode.nodeid,
                    node_name: snode.name,
                }
            };
        }
        node_initialize_related(snode, tg, nodes);
    }

    for(const conn of data.connections)
    {
        const c = new Connection;
        Object.assign(c, conn);
        store.commit(Mutations.setConnection, c);
    }

    for(const stl of data.timelines)
    {
        const tg = new Timeline;
        tl_deserialize(stl, tg, globalTags);
        store.commit(Mutations.setTimeline, tg);
    }

    const proj = new Project;
    proj_deserialize(data.project, proj);
    store.commit(Mutations.setProject, proj);
}